import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { asEnumerable } from 'linq-es2015';
import { Observable, of, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DateHelperService } from '../../shared/helpers/date-helper.service';
import { globalUtilsHelper } from '../../shared/helpers/global-utils-helper';
import { WlmResizeObserverService } from '../../shared/services/resize-observer.service';
import { MapTimeBarComponent } from '../components/map-time-bar/map-time-bar.component';
import {
  MapTopBarButtonsComponent,
  MapTopbarButtonsConfiguration,
} from '../components/map-top-bar-buttons/map-top-bar-buttons.component';
import { MapSidePanelSettings } from '../components/side-panel/map-side-panel-settings';
import { Leaflet } from '../leaflet-export';
import {
  LayerInfo,
  LayerInfoTypeEnum,
  LayerItem,
  LayerStyle,
  LayerTypeEnum,
} from '../models/layer-item';
import { LayerNameSpace } from '../models/layer-namespace';
import { MapDateRange } from '../models/map-date-range';
import { MapStep } from '../models/map-step';
import { SelectedLayerInfo } from '../models/selected-layer-info';
import { ExportedMapParamsService } from '../services/exported-map-params.service';
import { FeatureInfoService } from '../services/feature-info.service';
import { LayerService } from '../services/layers.service';
import { LeafletBetterWmsService } from '../services/leaflet-better-wms.service';
import { LeafletTimeBarService } from '../services/leaflet-time-bar.service';
import { LeafletTopBarService } from '../services/leaflet-top-bar.service';

@UntilDestroy()
@Component({
  selector: 'wlm-leaflet-map',
  templateUrl: './leaflet-map.component.html',
  styleUrls: ['./leaflet-map.component.scss'],
  providers: [LeafletTopBarService],
  standalone: false
})
export class LeafletMapComponent implements OnInit, AfterViewInit {
  private _resizeContainerSubs: Subscription;

  @Output() layerClicked = new EventEmitter<SelectedLayerInfo>();
  @Output() timeChanged = new EventEmitter<MapStep>();
  @Output() zoomChanged = new EventEmitter<number>();
  @Output() boundsChanged = new EventEmitter<any>();

  private _tooltipMetadata: any;
  selectedStep: Date;
  public get tooltipMetadata(): any {
    return this._tooltipMetadata;
  }
  @Input() public set tooltipMetadata(v: any) {
    this._tooltipMetadata = v;
  }

  private _layersNameSpaces: LayerNameSpace[];
  public get layersNameSpaces(): LayerNameSpace[] {
    return this._layersNameSpaces;
  }
  @Input() public set layersNameSpaces(value: LayerNameSpace[]) {
    this._layersNameSpaces = value;
    this.initMapLayers();
  }
  @Input() parentWidgetInstanceKey: string;
  @Input() mapDateRange: MapDateRange;
  @Input() topBarConfiguration: MapTopbarButtonsConfiguration;

  private _enableParamsExport = false;
  get enableParamsExport() {
    return this._enableParamsExport;
  }
  @Input() set enableParamsExport(value) {
    this._enableParamsExport = value;
    this._exportedMapParams.enableExport(this.enableParamsExport);
  }

  map;
  id: string;
  sidePanelId: string;
  timeBarId: string;
  topBarId: string;
  layers: LayerItem[];
  layersLatLong: LatLong[];
  readonly _initialZoom = 7;

  private _mapContainer: ElementRef<HTMLElement>;
  @ViewChild('mapContainer') set setmapContainer(mapContainer: ElementRef<any>) {
    if (mapContainer) {
      this._mapContainer = mapContainer;
      this._resizeContainerSubs?.unsubscribe();
      this.initMapLayers();

      this._resizeContainerSubs = this._resizeObserverService
        .observe({
          el: mapContainer.nativeElement,
        })
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          setTimeout(() => {
            this.map?.invalidateSize();
          }, 400);
        });
    }
  }

  @Input() sidePanelSettings: MapSidePanelSettings<any>;
  @Input() mapId: string;
  @ViewChild(MapTimeBarComponent) timeBar: MapTimeBarComponent;
  @ViewChild(MapTopBarButtonsComponent) topBar: MapTopBarButtonsComponent;

  constructor(
    private _betterWmsService: LeafletBetterWmsService,
    private _featureInfoService: FeatureInfoService,
    private _layerService: LayerService,
    private _resizeObserverService: WlmResizeObserverService,
    private _dateHelperService: DateHelperService,
    private _leafletTimeBarService: LeafletTimeBarService,
    private _leafletTopBarService: LeafletTopBarService,
    private _exportedMapParams: ExportedMapParamsService
  ) {
    this.id = globalUtilsHelper.generateGuid();
    this.sidePanelId = globalUtilsHelper.generateGuid();
    this.timeBarId = globalUtilsHelper.generateGuid();
    this.topBarId = globalUtilsHelper.generateGuid();
    this._betterWmsService.extends();
    this._leafletTimeBarService.extends();
    this._leafletTopBarService.extends();
  }

  ngOnInit(): void {
    this._betterWmsService.selectedLayer$
      .pipe(
        untilDestroyed(this),
        filter((layer) => {
          return layer.mapId === this.id;
        })
      )
      .subscribe({
        next: (layer: SelectedLayerInfo) => {
          const clonedLayer = new SelectedLayerInfo(layer);
          clonedLayer.parentKey = this.parentWidgetInstanceKey;
          clonedLayer.startDate = this.mapDateRange.startDate;
          clonedLayer.endDate = this.mapDateRange.endDate;
          this.layerClicked.emit(clonedLayer);
          this._exportedMapParams.onLayerChanged(clonedLayer);
        },
      });
  }

  ngAfterViewInit(): void {
    //this.initMapLayers();
  }

  private resetMap() {
    this.layers = [];
    this.map?.off();
    this.map?.remove();
  }

  private initMapLayers() {
    this.resetMap();

    if (!this._mapContainer?.nativeElement) {
      return;
    }

    this.map = Leaflet.map(this._mapContainer.nativeElement, {
      center: [0, 0],
      zoom: this._initialZoom,
      mapId: this.id,
      waterTimeBarOptions: {
        dataRange: this.mapDateRange,
        currentStep: Date.parse('2016-04-18'),
      },
      metadata: this.tooltipMetadata,
      zoomControl: false,
    });

    //const nodeLayer = this._layerService.getNodeLayer(`snapshotId_scenarioId`, 'test_layer');
    //this._layerService.getLineLayer(`snapshotId_scenarioId`, 'test_layer', this.map).subscribe(lineLayer => {
    const lineLayer = this._layerService.getLineLayer(`snapshotId_scenarioId`, 'test_layer');
    lineLayer.layerStyles.zIndex = 2;

    this.layers = [
      this.getBaseMapLayer(),
      //nodeLayer,
      lineLayer
    ];

    this.initMap();

    /*
    const nameSpaceFeatures$ = this.layersNameSpaces
      .map((x) => x.namespace)
      .map((namespace) => {

        // TODO: Call API
        return this._featureInfoService.getLayersByNamespace(namespace);
      });
    const layers = [];
    layers.push(this.getBaseMapLayer());

    forkJoin(nameSpaceFeatures$).subscribe({
      next: (data: any[]) => {
        const availableLayers: any[] = asEnumerable(data)
          .SelectMany((x) => {
            const layer = x['featureTypes']['featureType'];
            return layer;
          })
          .ToArray();

        let zIndex = 1;

        if (availableLayers) {
          const layersInfo$: Observable<any>[] = availableLayers.map((x) => {
            return this._featureInfoService.getLayerInfo(x.href); // Do not use this
          });

          const layerBounds = [];

          forkJoin(layersInfo$).subscribe({
            next: (layersInfo) => {
              layersInfo.forEach((layerInfo) => {
                const layerFeatures = layerInfo['featureType'];

                if (layerFeatures['latLonBoundingBox']) {
                  layerBounds.push(this.getLatLong(layerFeatures['latLonBoundingBox']));
                }

                if (layerFeatures?.name) {
                  const layerNameSpace = this.layersNameSpaces.find(
                    (x) => x.namespace == layerFeatures.namespace.name
                  );

                  const parsedInfo = JSON.parse(layerFeatures['abstract']);
                  const layerInfo = new LayerInfo({
                    elementType: parsedInfo.ElementType,
                    layerType: parsedInfo.LayerType,
                  });
                  const mapLayer = this.getLayerByName(
                    layerNameSpace.namespace,
                    layerNameSpace.name,
                    layerFeatures?.name,
                    layerFeatures?.title,
                    zIndex,
                    layerInfo
                  );
                  if (mapLayer) {
                    layers.push(mapLayer);
                    zIndex++;
                  }
                }
              });
              this.layers = [
                ...asEnumerable(layers)
                  .OrderByDescending((x) => x.layerStyles.zIndex)
                  .ToArray(),
              ];

              this.layersLatLong = layerBounds;

              this.initMap();
            },
            error: () => {
              this.initMap();
            },
          });
        }
      },
    });
    */
  }

  private initMap(): void {

    //const latLong = this.getCenterFromLatsLongs(this.layersLatLong);

    this.zoomChanged.emit(this._initialZoom);

    //this.map.panTo([latLong.lat, latLong.long]);

    asEnumerable(this.layers.map((x) => x.pane))
      .Distinct()
      .ToArray()
      .forEach((pane) => {
        this.map.createPane(pane);
        const mapPane = this.map.getPane(pane);
        mapPane.style.zIndex = this.layers.find((x) => x.pane == pane).layerStyles.zIndex;
      });

    //const group = Leaflet.featureGroup().addTo(this.map);

    this.layers.forEach((layerItem) => {
      //layerItem.layer.addTo(group);
      console.log("old map", layerItem.nativeLayer)
      layerItem.nativeLayer.addTo(this.map);
    });

    setTimeout(() => {
      this.getResults().subscribe(results => {
        this.layers.forEach((layerItem) => {
          const layer = layerItem.nativeLayer;

          if (layer.options?.filter) {

            layer.eachLayer((innerLayer: any) => {
              const id = innerLayer.feature.properties?.neId;
              const layerResults = results[id];

              if (layerResults) {
                innerLayer.feature.properties = {
                  ...(innerLayer.feature.properties ?? {}),
                  ...layerResults
                };

                const newStyles = layerItem.getDynamicStyles(innerLayer.feature);
                innerLayer.setStyle(newStyles);
              }
            });

            //const popup = Leaflet.popup().setContent(`Test: ${Math.floor(Math.random() * 10 + 1)}`);
            //layer.bindPopup(popup).openPopup();
          }

          // Filter example
          //if (layerItem.layer.options?.filter) {
          //const filter2 = (new Leaflet.Filter.And(layerItem.layer.options.filter, new Leaflet.Filter.EQ('neId', '0195fb31-8c76-4752-a1af-b9aaaf08f5e1'))).toGml();
          //layerItem.layer.clearLayers();
          //layerItem.layer.loadFeatures(filter2);
          //}
        });
      });
    }, 10000);

    //this.addSidePanel();
    //this.addTimeBar();
    //this.addTopBar();

    //this.bindEvents();
  }

  private _currentTimeStep: Date;
  private _currentSnapshotIds: string[];

  private onTimeStepChange(timeStep: Date): void {
    this._currentTimeStep = timeStep;
    // this.reloadResults();
  }

  private onSnapshotsChange(snapshotIds: string[]): void {
    this._currentSnapshotIds = snapshotIds;
    // this.reloadResults();
  }

  private getResults(): Observable<any> {
    const results = {
      '0195fb31-8c76-4752-a1af-b9aaaf08f5e1': {
        "pups": 1,
        "cups": 1,
      }
    };
    return of(results);
  }

  private getRandom(): number {
    return Math.floor(Math.random() * 10 + 1);
  }

  private bindEvents(): void {
    this.map.waterTimeBar.on('currentStepChange', this.stepChanged, this);

    this.map.on('zoomend', this.onZoomEnd);
    this.map.on('moveend', this.onMapMoveEnd);
  }

  private stepChanged(event: { step: Date }) {
    if (this.selectedStep?.toString() == event?.step?.toString()) {
      return;
    }

    this.selectedStep = event.step;
    // Filter by temporal layers and thematic layer
    const temporalLayers = this.layers.filter(
      (x) =>
        x.layerType == LayerTypeEnum.NonTiledLayer || x.layerType == LayerTypeEnum.TemporalLayer
    );
    temporalLayers.forEach((layerItem) => {
      const mapLayer: any = this.findMapLayer(layerItem);

      const params = {
        time: this._dateHelperService.toApiFormat(new Date(event.step)),
      };
      if (mapLayer) {
        mapLayer.setParams(params, false);
      }

      layerItem.nativeLayer.setParams(params, false);
    });

    const payload = new SelectedLayerInfo({
      step: new Date(event.step),
      parentKey: this.parentWidgetInstanceKey,
    });
    this.timeChanged.emit(payload);
    this._exportedMapParams.onStepChanged(payload);
  }

  private findMapLayer(layerItem: LayerItem): any {
    return Object.values(this.map['_layers']).find(
      (layer) => (layer as any).options.pane == layerItem.pane
    );
  }

  private onZoomEnd = (event): void => {
    const zoom = this.map.getZoom();
    this.zoomChanged.emit(zoom);
    this._exportedMapParams.onZoomChanged(zoom);
  };

  private onMapMoveEnd = (event): void => {
    const bounds = this.map.getBounds();
    this.boundsChanged.emit(bounds);
    this._exportedMapParams.onBoundsChanged(bounds);
  };

  private getLatLong({ maxx, minx, miny, maxy }) {
    const long = (Number(maxx) + Number(minx)) / 2;
    const lat = (Number(maxy) + Number(miny)) / 2;

    return new LatLong({ lat, long });
  }

  private getCenterFromLatsLongs(latLongs: LatLong[]): LatLong {
    const lat =
      asEnumerable(latLongs)
        .Select((x) => x.lat)
        .Sum() / latLongs.length;
    const long =
      asEnumerable(latLongs)
        .Select((x) => x.long)
        .Sum() / latLongs.length;

    return new LatLong({ lat, long });
  }

  private getBaseMapLayer() {
    return new LayerItem({
      name: 'BaseMap',
      pane: 'baseMap',
      layerStyles: new LayerStyle({ zIndex: 0 }),
      namespace: 'baselayer',
      nativeLayer: Leaflet.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        // layers: 'OSM-WMS',
        // layerId: 'a',
        autoZIndex: 0,
        srs: 'EPSG:4326',
        pane: 'baseMap',
      }),
      layerInfo: new LayerInfo({ elementType: undefined, layerType: LayerInfoTypeEnum.Topology }),
    });
  }

  private getLayerByName(
    namespace: string,
    namespaceTitle: string,
    layerName: string,
    layerTitle: string,
    index: number,
    layerInfo: LayerInfo
  ) {
    if (layerName.includes('heatmap')) {
      const nodeLayer = this._layerService.getHeatmapLayer(
        namespace,
        namespaceTitle,
        layerName,
        layerTitle,
        index,
        this.id,
        layerInfo
      );
      return nodeLayer;
    } else if (!layerName.includes('DONTSHOW')) {
      const lineLayer = this._layerService.getWmsLayer(
        namespace,
        namespaceTitle,
        layerName,
        layerTitle,
        index,
        this.id,
        layerInfo
      );
      return lineLayer;
    }
  }

  private addSidePanel() {
    Leaflet.control.sidebar({ container: this.sidePanelId }).addTo(this.map);
  }

  private addTimeBar(): void {
    const nativeElement = this.timeBar.getNativeElement();
    Leaflet.control
      .waterTimeBarControl({
        container: nativeElement,
      })
      .addTo(this.map);
  }

  private addTopBar(): void {
    const nativeElement = this.topBar.getNativeElement();
    Leaflet.control
      .topBarControl({
        container: nativeElement,
      })
      .addTo(this.map);
  }
}

export class LatLong {
  lat: number;
  long: number;

  constructor(init: Partial<LatLong>) {
    Object.assign(this, init);
  }
}
